// The Conversations panel as a real WhatsApp (owner, 2026-09-25, A2).
//
// « I want to work entirely from the system, never switching to my phone. »
//   SEND    text · voice note · image · video · any file · quote-reply
//   RECEIVE voice notes with their transcript · images and video inline, full
//           size on click · any file to download · who wrote every message
//   LOOK    WhatsApp's own: the chat background, green outgoing bubbles, grey
//           bars, blue ticks, day separators (owner: « use the whatsapp green
//           and background … ui of whatsapp »). Colours only — no WhatsApp
//           image is copied; the tails and icons are drawn here.
//   RULES   everything goes through the relay; sending from here pauses the
//           agent on that chat; the words go exactly as typed.
//   DELETE  (2026-09-29) « Supprimer pour tout le monde » on our own messages
//           for 48 h, after a yes to one question; a deleted one stays in the
//           thread as « 🚫 Message supprimé », its words small. No pause.
//   REACT   (2026-10-18) ❤️ 👍 🙏 😂 😮 + any emoji on a message, sent as a real
//           WhatsApp reaction; hers and ours as chips under the bubble; an
//           emoji button in the composer. A reaction is not a reply: no pause.
//           Only where `panel_reactions` is on for the chat (the worker says).
//
// Who wrote it, what kind it is, what it quotes and its ticks are computed by
// the worker (lib/conversationThread.ts); this file only draws them.
//
// THE SIMULATOR DRAWS WITH IT TOO (owner, 2026-10-01: « use the same simulator
// as the conversation — same everything … like the Conversations WhatsApp
// view we have now. Use the same one »). `ThreadFrame` (the thread: day
// separators, bubbles, images full size, playable videos, voice notes with
// their transcript, deletes) and `Composer` (text, ⌘V a picture, the
// paperclip, the microphone) are shared; what Send and Delete DO is the
// caller's — the relay here, /simulate and nothing real in the Simulator.
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { whatsappApi, type WhatsAppMessage } from '@/lib/api';
import { useCan } from '@/lib/actions';
import { useT } from '@/lib/i18n';
import { groupByDay, timeHM, ticksFor, senderLabel, sentAgo, canOfferDelete, isDeletedForEveryone, kindForFile, openPending, pendingAsMessages, type PendingSend } from './threadFormat';
import { useVoiceRecorder } from './useVoiceRecorder';

// ─── WhatsApp's palette — light AND night: waTheme.css (2026-10-02) ─────────
// Every colour of the thread is a --wa-* variable: light values are WhatsApp's
// own, night ones the warm-dark take (waTheme.guard.test.tsx pins both).
import './waTheme.css';
import { SchedulePicker, ScheduledStrip, ScheduleIcon, useWhen } from './SchedulePicker';
import { EmojiPicker, QUICK_REACTIONS, insertAtCursor } from './EmojiPicker';
import { savedScheduleMs } from '@/lib/sendVisibility';

const v = (name: string) => `var(--wa-${name})`;
const WA = {
  wallpaper: v('wallpaper'),
  bar: v('bar'),
  field: v('field'),
  inBubble: v('in'),
  outBubble: v('out'),
  inInset: v('in-inset'),
  outInset: v('out-inset'),
  outFile: v('out-file'),
  deleted: v('deleted'),
  text: v('text'),
  meta: v('meta'),
  transcript: v('transcript'),
  icon: v('icon'),
  tickGrey: v('tick-grey'),
  tickBlue: v('tick-blue'),
  green: v('green'),
  greenDark: v('green-dark'),
  sent: v('sent'),
  error: v('error'),
  voiceIn: v('voice-in'),
  chip: v('chip'),
  chipInk: v('chip-ink'),
  empty: v('empty'),
  quoteBar: v('quote-bar'),
} as const;
/** Sender names, coloured the way WhatsApp colours names in a group. */
const NAME_COLOR: Record<string, string> = { client: v('name-client'), agent: v('name-agent'), person: v('name-person'), auto: v('name-agent') };
const FONT = '"Segoe UI", "Helvetica Neue", Helvetica, "Lucida Grande", Arial, Ubuntu, Cantarell, "Fira Sans", sans-serif';

const atOf = (m: WhatsAppMessage) => m.wa_timestamp ?? m.created_at;

function Tail({ side, color }: { side: 'in' | 'out'; color: string }) {
  // A small drawn tail on the bubble's top corner — WhatsApp's silhouette.
  return (
    <svg aria-hidden width="8" height="13" viewBox="0 0 8 13"
      className={`absolute top-0 ${side === 'in' ? '-left-2' : '-right-2'}`}>
      <path d={side === 'in' ? 'M8 0 L8 13 L0 0 Z' : 'M0 0 L0 13 L8 0 Z'} style={{ fill: color }} />
    </svg>
  );
}

function Icon({ name, className = '' }: { name: 'clip' | 'mic' | 'send' | 'play' | 'pause' | 'x' | 'reply' | 'trash' | 'smile'; className?: string }) {
  const p: Record<string, string> = {
    clip: 'M16.5 6.5 9 14a2.12 2.12 0 0 0 3 3l7.5-7.5a4.24 4.24 0 0 0-6-6L6 11a6.36 6.36 0 0 0 9 9l6.5-6.5',
    mic: 'M12 15a3 3 0 0 0 3-3V6a3 3 0 1 0-6 0v6a3 3 0 0 0 3 3Zm6-3a6 6 0 0 1-12 0M12 18v3',
    send: 'M4 12 20 4l-4 16-4-6-8-2Z',
    play: 'M8 5v14l11-7L8 5Z',
    pause: 'M7 5h4v14H7zM13 5h4v14h-4z',
    x: 'M6 6l12 12M18 6 6 18',
    reply: 'M10 9V5l-7 7 7 7v-4c5 0 8 1.5 11 5-1-5-4-10-11-11Z',
    trash: 'M4 7h16M9 7V4.5h6V7M6.5 7l.9 12.1A2 2 0 0 0 9.4 21h5.2a2 2 0 0 0 2-1.9L17.5 7M10 11v6M14 11v6',
    smile: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM8.5 14.5s1.2 1.8 3.5 1.8 3.5-1.8 3.5-1.8M9 9.5h.01M15 9.5h.01',
  };
  const filled = name === 'send' || name === 'play' || name === 'pause' || name === 'reply';
  return (
    <svg aria-hidden viewBox="0 0 24 24" className={className}
      fill={filled ? 'currentColor' : 'none'} stroke={filled ? 'none' : 'currentColor'} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
      <path d={p[name]} />
    </svg>
  );
}

const mmss = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

/**
 * ⚠️ A PLAYER KEEPS THE LINK IT STARTED WITH (owner, 2026-10-10: « it cuts in
 * the middle »). The thread is re-read every 15 s and every read signs a FRESH
 * link; a new `src` on a playing <audio> or <video> reloads it from 0, so it
 * stopped wherever the next read landed. The newest link is taken only when
 * the kept one fails — signed links expire after an hour.
 */
function usePinnedSrc(url: string): { src: string; onError: () => void } {
  const [src, setSrc] = useState(url);
  const latest = useRef(url);
  latest.current = url;
  return { src, onError: () => { if (latest.current !== src) setSrc(latest.current); } };
}

/** WhatsApp's voice-note bubble: a round play button, progress, time. */
function VoicePlayer({ url, tone }: { url: string; tone: string }) {
  const ref = useRef<HTMLAudioElement | null>(null);
  const { src, onError } = usePinnedSrc(url);
  const [playing, setPlaying] = useState(false);
  const [pos, setPos] = useState(0);
  const [dur, setDur] = useState(0);
  return (
    <div className="flex items-center gap-2.5 w-64 max-w-full py-1">
      <audio ref={ref} src={src} preload="metadata"
        onError={() => { onError(); setPlaying(false); }}
        onPause={() => setPlaying(false)}
        onPlay={() => setPlaying(true)}
        onLoadedMetadata={(e) => setDur(Number.isFinite(e.currentTarget.duration) ? e.currentTarget.duration : 0)}
        onTimeUpdate={(e) => setPos(e.currentTarget.currentTime)}
        onEnded={() => { setPlaying(false); setPos(0); }} />
      <button type="button" aria-label={playing ? 'Pause' : 'Écouter'}
        onClick={() => { const a = ref.current; if (!a) return; if (a.paused) { void a.play(); setPlaying(true); } else { a.pause(); setPlaying(false); } }}
        className="h-9 w-9 shrink-0 rounded-full flex items-center justify-center text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
        style={{ backgroundColor: tone }}>
        <Icon name={playing ? 'pause' : 'play'} className="h-4 w-4" />
      </button>
      <div className="flex-1 min-w-0">
        <input type="range" min={0} max={dur || 0} step={0.1} value={Math.min(pos, dur || 0)} aria-label="Position"
          onChange={(e) => { const a = ref.current; if (a) { a.currentTime = Number(e.target.value); setPos(a.currentTime); } }}
          className="w-full h-1 cursor-pointer" style={{ accentColor: tone }} />
        <p className="text-[11px] tabular-nums mt-0.5" style={{ color: WA.meta }}>{mmss(playing || pos ? pos : dur)}</p>
      </div>
    </div>
  );
}

/** A client's video — the same pinned link as a voice note (`usePinnedSrc`). */
function VideoPlayer({ url }: { url: string }) {
  const { src, onError } = usePinnedSrc(url);
  return <video data-adm-media src={src} onError={onError} controls preload="metadata" className="max-h-72 max-w-[300px] rounded-md bg-black" />;
}

function Lightbox({ src, alt, onClose }: { src: string; alt: string; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div role="dialog" aria-modal="true" aria-label={alt}
      className="fixed inset-0 z-50 bg-[#0b141a]/95 flex items-center justify-center p-6 cursor-zoom-out" onClick={onClose}>
      <img data-adm-media src={src} alt={alt} className="max-h-full max-w-full object-contain" />
      <a href={src} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
        className="absolute top-4 right-4 text-[13px] text-white/90 hover:text-white bg-white/10 hover:bg-white/20 rounded-full px-3.5 py-1.5">
        Ouvrir l’original
      </a>
    </div>
  );
}

function FileChip({ url, name, ours }: { url: string; name: string; ours: boolean }) {
  const { t } = useT();
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" download={name || undefined}
      className="flex items-center gap-3 rounded-md px-3 py-2.5 min-w-[220px]"
      style={{ backgroundColor: ours ? WA.outFile : WA.inInset }}>
      <span aria-hidden className="text-[26px] leading-none">📄</span>
      <span className="min-w-0 flex-1">
        <span className="block text-[13.5px] truncate max-w-[220px]" style={{ color: WA.text }}>{name || t('Fichier')}</span>
        <span className="block text-[11.5px]" style={{ color: WA.meta }}>{t('Télécharger')}</span>
      </span>
    </a>
  );
}

function MediaView({ m, ours, onZoom }: { m: WhatsAppMessage; ours: boolean; onZoom: (src: string, alt: string) => void }) {
  const { t } = useT();
  const [broken, setBroken] = useState(false);
  const url = m.media_url ?? null;
  const name = m.media_filename ?? m.body ?? '';
  if (!url) {
    // 2026-10-24: never an empty bubble, never a sticker's file code. A file the
    // gateway could not download is fetched again within minutes (VPS
    // media-rescue); an ANIMATED sticker (application/was) has no image a
    // browser can draw.
    const animated = m.kind === 'sticker' && /application\/was/i.test(m.media_mimetype ?? '');
    const missing: Partial<Record<NonNullable<WhatsAppMessage['kind']>, string>> = {
      image: t('📷 Photo pas encore reçue — récupération automatique en cours'),
      video: t('🎬 Vidéo pas encore reçue — récupération automatique en cours'),
      document: t('📄 Fichier pas encore reçu — récupération automatique en cours'),
      sticker: animated ? t('🙂 Sticker animé (non affichable ici)') : t('🙂 Sticker'),
      audio: t('🎵 Audio pas encore reçu — récupération automatique en cours'),
      voice: t('🎤 Message vocal pas encore reçu — récupération automatique en cours'),
    };
    const label = m.kind ? missing[m.kind] : undefined;
    if (label) return <p className="text-[12.5px] italic" style={{ color: WA.meta }}>{label}{name && m.kind !== 'sticker' ? ` — ${name}` : ''}</p>;
    if (!m.has_media) return null;
    return <p className="text-[12.5px] italic" style={{ color: WA.meta }}>{t('Fichier non disponible ici')}{name ? ` — ${name}` : ''}</p>;
  }
  switch (m.kind) {
    case 'image':
    case 'sticker':
      // A browser cannot draw every photo (HEIC from iPhones): the file is
      // still offered, never a broken image.
      if (broken) return <FileChip url={url} name={name} ours={ours} />;
      return (
        <button type="button" data-adm-media onClick={() => onZoom(url, name || t('image'))}
          className="block cursor-zoom-in rounded-md overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00a884]">
          <img src={url} alt={name || t('image')} loading="lazy" onError={() => setBroken(true)}
            className={m.kind === 'sticker' ? 'w-32 h-32 object-contain' : 'max-h-72 w-auto max-w-[300px] object-cover'} />
        </button>
      );
    case 'video':
      return <VideoPlayer url={url} />;
    case 'voice':
    case 'audio':
      return <VoicePlayer url={url} tone={ours ? WA.greenDark : WA.voiceIn} />;
    default:
      return <FileChip url={url} name={name} ours={ours} />;
  }
}

/** A deleted message: WhatsApp's own greyed bubble, not the green one. */
const DELETED_BG = WA.deleted;

/** What a caller may hang on one message. Conversations: quote-reply and
 *  « Supprimer pour tout le monde ». The Simulator: its own delete, its tags
 *  above the words and « corriger » below them (2026-10-01). */
export interface BubbleOptions {
  /** Quote-reply — offered only on a message WhatsApp gave an id. */
  onReply?: (m: WhatsAppMessage) => void;
  /** Present only when a delete may be offered here. */
  onDelete?: (m: WhatsAppMessage) => void;
  /** What that delete is called — « Supprimer pour tout le monde » by default. */
  deleteLabel?: string;
  /** The request for THIS message is on its way to the worker. */
  deleting?: boolean;
  /** The worker's refusal for THIS message (too late, no WhatsApp id yet…). */
  deleteError?: string | null;
  /** Under the sender's name, above the words. */
  top?: ReactNode;
  /** Under the words, above the time. */
  bottom?: ReactNode;
  /** Ringed — the one being worked on (the reply being corrected). */
  highlight?: boolean;
  /** ❤️ on this message (2026-10-18) — present only where reactions are on. '' takes ours back. */
  onReact?: (m: WhatsAppMessage, emoji: string) => void;
  /** The worker's refusal of a reaction on THIS message. */
  reactError?: string | null;
}

/** The chips under a bubble: hers first, ours last; ours outlined. */
function ReactionChips({ list, ours }: { list: NonNullable<WhatsAppMessage['reactions']>; ours: boolean }) {
  const { t } = useT();
  return (
    <div data-reactions className={`-mt-1.5 relative z-[1] flex gap-1 ${ours ? 'justify-end pr-2' : 'justify-start pl-2'}`}>
      {list.map((r, i) => (
        <span key={`${r.by}-${i}`} data-reaction={r.emoji} data-by={r.by}
          title={r.by === 'staff' ? t('Notre réaction') : t('Réaction de la cliente')}
          className="text-[14px] leading-none rounded-full px-1.5 py-1 shadow-[0_1px_1px_rgba(11,20,26,0.18)] border"
          style={{ backgroundColor: WA.inBubble, borderColor: r.by === 'staff' ? WA.green : 'transparent' }}>
          {r.emoji}
        </span>
      ))}
    </div>
  );
}

/** The hover « React » button and its palette: the five quick ones, « + » for any emoji. */
function ReactButton({ m, onReact }: { m: WhatsAppMessage; onReact: (m: WhatsAppMessage, emoji: string) => void }) {
  const { t } = useT();
  const [open, setOpen] = useState<'quick' | 'all' | null>(null);
  const mine = (m.reactions ?? []).find((r) => r.by === 'staff')?.emoji ?? null;
  // The emoji already ours, pressed again, takes it back — as in WhatsApp.
  const pick = (e: string) => { setOpen(null); onReact(m, e === mine ? '' : e); };
  return (
    <div className="relative self-center">
      <button type="button" onClick={() => setOpen((o) => (o ? null : 'quick'))} data-react-toggle
        title={t('Réagir')} aria-label={t('Réagir')} aria-expanded={Boolean(open)}
        className={`${open ? 'opacity-100' : 'opacity-0'} group-hover:opacity-100 focus:opacity-100 h-7 w-7 rounded-full bg-white/80 dark:bg-[var(--wa-field)] shadow flex items-center justify-center transition-opacity duration-150`}
        style={{ color: WA.meta }}>
        <Icon name="smile" className="h-4 w-4" />
      </button>
      {open === 'quick' && (
        <div role="menu" data-react-palette className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 z-30 flex items-center gap-0.5 rounded-full bg-white dark:bg-[var(--wa-bar)] shadow-lg border border-black/10 dark:border-[var(--wa-frame)] px-1.5 py-1">
          {QUICK_REACTIONS.map((e) => (
            <button key={e} type="button" role="menuitem" data-quick={e} onClick={() => pick(e)} aria-label={e}
              className={`h-8 w-8 rounded-full text-[20px] leading-none cursor-pointer hover:bg-black/5 dark:hover:bg-white/10 ${mine === e ? 'bg-black/10 dark:bg-white/15' : ''}`}>
              {e}
            </button>
          ))}
          <button type="button" role="menuitem" data-react-more onClick={() => setOpen('all')} aria-label={t('Autre emoji')} title={t('Autre emoji')}
            className="h-8 w-8 rounded-full text-[18px] leading-none cursor-pointer hover:bg-black/5 dark:hover:bg-white/10" style={{ color: WA.meta }}>
            +
          </button>
        </div>
      )}
      {open === 'all' && (
        <EmojiPicker className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2" label={t('Réagir')} onPick={pick} onClose={() => setOpen(null)} />
      )}
    </div>
  );
}

function Bubble({ m, first, onZoom, onReply, onDelete, deleteLabel, deleting = false, deleteError = null, top = null, bottom = null, highlight = false, onReact, reactError = null }: {
  m: WhatsAppMessage; first: boolean; onZoom: (src: string, alt: string) => void;
} & BubbleOptions) {
  const { t } = useT();
  const sender = m.sender ?? (m.direction === 'in' ? 'client' : 'agent');
  const ours = sender !== 'client';
  const row = `flex gap-1.5 ${ours ? 'justify-end' : 'justify-start'} ${first ? 'mt-2.5' : 'mt-0.5'}`;

  // ─── DELETED FOR EVERYONE — gone from her phone, kept here ──────────────────
  // « 🚫 Message supprimé », greyed; the words stay, small, for the team's
  // memory of what was taken back. Nothing to reply to, nothing to delete.
  if (isDeletedForEveryone(m)) {
    const words = m.body || m.transcript || (m.has_media ? t('(fichier)') : '');
    return (
      <div data-message={m.wa_message_id} className={row}>
        <div className={`relative max-w-[82%] md:max-w-[65%] px-2 pt-1.5 pb-1 rounded-lg shadow-[0_1px_0.5px_rgba(11,20,26,0.13)] ${first ? (ours ? 'rounded-tr-none' : 'rounded-tl-none') : ''}`}
          style={{ backgroundColor: DELETED_BG, color: WA.meta }}>
          {first && <Tail side={ours ? 'out' : 'in'} color={DELETED_BG} />}
          <p className="text-[13.5px] italic leading-5 pr-12"
            title={m.delete_requested_by ? t('Supprimé par {who}', { who: m.delete_requested_by }) : undefined}>
            <span aria-hidden>🚫 </span>{t('Message supprimé')}
          </p>
          {words && (
            <p data-deleted-text title={t('Texte d’origine, gardé pour l’équipe')}
              className="text-[11.5px] leading-4 whitespace-pre-wrap break-words line-clamp-3 mt-0.5 pr-12 opacity-80">{words}</p>
          )}
          <span className="float-right -mt-3 ml-2 text-[11px] leading-none tabular-nums select-none relative top-1">{timeHM(atOf(m))}</span>
          <span className="clear-both block" />
        </div>
      </div>
    );
  }

  const ticks = ours ? ticksFor(m.status) : null;
  const voice = m.kind === 'voice' || m.kind === 'audio';
  const bg = ours ? WA.outBubble : WA.inBubble;
  const replyBtn = m.quotable_id && onReply ? (
    <button type="button" onClick={() => onReply(m)} title={t('Répondre à ce message')} aria-label={t('Répondre à ce message')}
      className="self-center opacity-0 group-hover:opacity-100 focus:opacity-100 h-7 w-7 rounded-full bg-white/80 dark:bg-[var(--wa-field)] shadow flex items-center justify-center transition-opacity duration-150"
      style={{ color: WA.meta }}>
      <Icon name="reply" className="h-4 w-4" />
    </button>
  ) : null;
  // Beside the reply arrow, on hover — the way WhatsApp keeps it out of the way.
  const deleteName = deleteLabel ?? t('Supprimer pour tout le monde');
  const deleteBtn = onDelete ? (
    <button type="button" onClick={() => onDelete(m)} disabled={deleting}
      title={deleteName} aria-label={deleteName}
      className="self-center opacity-0 group-hover:opacity-100 focus:opacity-100 h-7 w-7 rounded-full bg-white/80 dark:bg-[var(--wa-field)] shadow flex items-center justify-center transition-opacity duration-150 hover:text-[var(--wa-error)]"
      style={{ color: WA.meta }}>
      <Icon name="trash" className="h-4 w-4" />
    </button>
  ) : null;
  const fileNameIsBody = m.has_media && !!m.body && m.body === (m.media_filename ?? m.body) && /\.[a-z0-9]{2,5}$/i.test(m.body);
  // ❤️ — only on a message WhatsApp gave an id, and only where reactions are on.
  const reactBtn = onReact && m.quotable_id ? <ReactButton m={m} onReact={onReact} /> : null;
  const chips = onReact && m.reactions?.length ? m.reactions : null;
  const bubble = (
      <div className={`relative ${chips ? 'max-w-full' : 'max-w-[82%] md:max-w-[65%]'} px-2 pt-1.5 pb-1 rounded-lg shadow-[0_1px_0.5px_rgba(11,20,26,0.13)] ${first ? (ours ? 'rounded-tr-none' : 'rounded-tl-none') : ''} ${highlight ? 'ring-2 ring-brand-400' : ''}`}
        style={{ backgroundColor: bg, color: WA.text }}>
        {first && <Tail side={ours ? 'out' : 'in'} color={bg} />}
        <p className="text-[12.8px] font-medium leading-5" style={{ color: NAME_COLOR[sender] }}>
          {t(senderLabel(sender))}{sender === 'client' && m.push_name ? ` · ${m.push_name}` : ''}
        </p>
        {top}
        {m.quoted_body && (
          <div className="mb-1 rounded-md overflow-hidden flex" style={{ backgroundColor: ours ? WA.outInset : WA.inInset }}>
            <span className="w-1 shrink-0" style={{ backgroundColor: WA.quoteBar }} />
            <p className="px-2 py-1 text-[12.5px] line-clamp-3" style={{ color: WA.meta }}>{m.quoted_body}</p>
          </div>
        )}
        <MediaView m={m} ours={ours} onZoom={onZoom} />
        {m.body && !fileNameIsBody && (
          // Line breaks are part of the message — WhatsApp shows them. A file
          // sent « as a document » carries its NAME as the body: not repeated.
          <p className="text-[14.2px] leading-[19px] whitespace-pre-wrap break-words pr-14">{m.body}</p>
        )}
        {voice && m.transcript && (
          <p className="text-[12.5px] leading-snug whitespace-pre-wrap break-words mt-0.5 pr-14" style={{ color: WA.transcript }}>
            <span className="font-medium">{t('Transcription')} :</span> {m.transcript}
          </p>
        )}
        {voice && !m.transcript && m.transcript_error && (
          // Said, never left as « en cours » for ever (the Simulator, 2026-10-01).
          <p className="text-[12px] leading-snug pr-14" style={{ color: WA.error }}>{t('Transcription impossible :')} {m.transcript_error}</p>
        )}
        {voice && !m.transcript && !m.transcript_error && m.direction === 'in' && (
          <p className="text-[12px] italic pr-14" style={{ color: WA.meta }}>{t('Transcription en cours…')}</p>
        )}
        {(m.delete_state === 'pending' || deleting) && (
          <p className="text-[11.5px] italic pr-14" style={{ color: WA.meta }}>{t('Suppression en cours…')}</p>
        )}
        {m.delete_state === 'failed' && !deleting && (
          <p className="text-[11.5px] leading-snug pr-14 mt-0.5" style={{ color: WA.error }}>{t('Suppression échouée :')} {m.delete_error ?? ''}</p>
        )}
        {deleteError && (
          <p role="alert" className="text-[11.5px] leading-snug pr-14 mt-0.5" style={{ color: WA.error }}>{t('Suppression impossible :')} {deleteError}</p>
        )}
        {reactError && (
          <p role="alert" className="text-[11.5px] leading-snug pr-14 mt-0.5" style={{ color: WA.error }}>{t('Réaction impossible :')} {reactError}</p>
        )}
        {bottom}
        <span className="float-right -mt-3 ml-2 flex items-center gap-1 text-[11px] leading-none tabular-nums select-none relative top-1" style={{ color: WA.meta }}>
          {timeHM(atOf(m))}
          {ticks && (
            <span title={t(ticks.label)} aria-label={t(ticks.label)} className="text-[12px] font-semibold tracking-[-0.18em]"
              style={{ color: ticks.tone === 'blue' ? WA.tickBlue : ticks.tone === 'red' ? WA.error : WA.tickGrey }}>
              {ticks.glyph}
            </span>
          )}
        </span>
        <span className="clear-both block" />
      </div>
  );
  return (
    <div data-message={m.wa_message_id} className={`group ${row}`}>
      {ours && deleteBtn}
      {ours && reactBtn}
      {ours && replyBtn}
      {chips ? (
        <div className={`flex flex-col max-w-[82%] md:max-w-[65%] ${ours ? 'items-end' : 'items-start'}`}>
          {bubble}
          <ReactionChips list={chips} ours={ours} />
        </div>
      ) : bubble}
      {!ours && replyBtn}
      {!ours && reactBtn}
      {!ours && deleteBtn}
    </div>
  );
}

const fmtSize = (n: number) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} Mo` : `${Math.max(1, Math.round(n / 1024))} Ko`);

/** The pictures on a paste — a screenshot, a copied image — in order. `files`
 *  lists distinct files; `items` can hold ONE picture in several formats, so it
 *  is only the fallback and never yields more than one. */
function pastedImages(data: DataTransfer | null): File[] {
  const isImage = (f: File | null | undefined): f is File => !!f && f.type.startsWith('image/');
  const files = Array.from(data?.files ?? []).filter(isImage);
  if (files.length) return files;
  const item = Array.from(data?.items ?? []).find((i) => i.kind === 'file' && i.type.startsWith('image/'));
  const one = item?.getAsFile();
  return isImage(one) ? [one] : [];
}

/** The words on a paste — unless they are only the pasted files' own names: a
 *  file copied in Finder carries its name as text, a label, not a caption. */
function pastedWords(data: DataTransfer | null): string {
  const text = data?.getData('text/plain') ?? '';
  const names = new Set(Array.from(data?.files ?? []).map((f) => f.name));
  const lines = text.split(/[\r\n]+/).map((l) => l.trim()).filter(Boolean);
  return lines.length && lines.every((l) => names.has(l)) ? '' : text;
}

/** Text a button put in the box (the order card's recommended messages). The
 *  nonce makes the same text twice a second fill. Never sends anything. */
export type ComposerPrefill = { text: string; nonce: number } | null;

/** What the person composed: words, and at most one file or one recording. */
export interface Outgoing {
  text: string;
  file: File | null;
  voice: { blob: Blob; mime: string } | null;
  /** The message this answers (Conversations' quote-reply). */
  quotedId: string | null;
}

/**
 * The box at the bottom: text, ⌘V a picture, the paperclip, the microphone.
 * What Send DOES is `onSend` — the relay for Conversations, /simulate for the
 * Simulator (2026-10-01). A send that fails keeps what was composed and says
 * why on « Échec de l'envoi » — the same line whoever refused it.
 */
export function Composer({ chatId, onSend, onSchedule, onSent, hint, sentBadge, suggestion = '', prefill = null, replyTo = null, onClearReply, busy = false, emoji = false }: {
  /** The conversation this box writes to; a new one clears it. */
  chatId: string;
  onSend: (out: Outgoing) => Promise<unknown>;
  /** ⏰ The clock next to Send (2026-10-10). Absent → no clock. Text only. */
  onSchedule?: (out: Outgoing, sendAtIso: string) => Promise<unknown>;
  onSent?: () => void;
  /** The grey line under the box: what Send does here, and the shortcuts. */
  hint: ReactNode;
  /** Shown while the box is exactly as the last send left it. */
  sentBadge?: (sent: { at: number }) => ReactNode;
  suggestion?: string;
  prefill?: ComposerPrefill;
  replyTo?: WhatsAppMessage | null;
  onClearReply?: () => void;
  /** Something else is on its way (the agent « écrit… »): nothing more goes until it lands. */
  busy?: boolean;
  /** The emoji button between the paperclip and the box (2026-10-18). Off by default. */
  emoji?: boolean;
}) {
  const { t } = useT();
  // What the person will send — seeded from the assistant's suggestion, then
  // theirs: `touched` stops a refetch overwriting a reply half-typed.
  const [text, setText] = useState('');
  const [touched, setTouched] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  // The picture a paste of SEVERAL arrived with: its one-line note shows only
  // while that picture is the one attached, so it leaves with it.
  const [firstOfMany, setFirstOfMany] = useState<File | null>(null);
  const [sent, setSent] = useState<{ at: number } | null>(null);
  const [scheduledFor, setScheduledFor] = useState<number | null>(null);
  const [picking, setPicking] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const when = useWhen();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const areaRef = useRef<HTMLTextAreaElement | null>(null);
  const voice = useVoiceRecorder();

  useEffect(() => { setText(''); setTouched(false); setFile(null); setSent(null); voice.discard(); }, [chatId]); // eslint-disable-line react-hooks/exhaustive-deps
  // ⏰ A new conversation: no schedule badge, no open picker.
  useEffect(() => { setScheduledFor(null); setPicking(false); }, [chatId]);
  useEffect(() => { if (!touched) setText(suggestion); }, [suggestion, touched]);
  // A recommended message from the order card: into the box, cursor at the end,
  // for the person to read and send. `touched` so no refetch overwrites it.
  useEffect(() => {
    if (!prefill) return;
    setText(prefill.text); setTouched(true); setSent(null);
    requestAnimationFrame(() => {
      const el = areaRef.current;
      if (el) { el.focus(); el.setSelectionRange(el.value.length, el.value.length); }
    });
  }, [prefill?.nonce]); // eslint-disable-line react-hooks/exhaustive-deps

  const done = () => {
    setSent({ at: Date.now() });
    setText(''); setTouched(true); setFile(null); voice.discard(); onClearReply?.();
    onSent?.();
  };

  const sendMut = useMutation({
    mutationFn: () => onSend({
      text,
      file,
      voice: voice.recording ? { blob: voice.recording.blob, mime: voice.recording.mime } : null,
      quotedId: replyTo?.quotable_id ?? null,
    }),
    onSuccess: () => { setScheduledFor(null); done(); },
  });
  // ⏰ Same box, same words — they leave at the chosen time instead of now.
  const scheduleMut = useMutation({
    mutationFn: (ms: number) => onSchedule!({ text, file: null, voice: null, quotedId: replyTo?.quotable_id ?? null }, new Date(ms).toISOString()),
    onSuccess: (r, ms) => {
      setPicking(false);
      setText(''); setTouched(true); setSent(null); onClearReply?.();
      // The time the SERVER saved, not the one picked (2026-10-15).
      setScheduledFor(savedScheduleMs(r, ms));
      onSent?.();
    },
  });
  const textOnly = !file && !voice.recording && voice.state !== 'recording';

  const hasContent = Boolean(voice.recording) || Boolean(file) || Boolean(text.trim());
  const pending = sendMut.isPending || busy;
  const canSend = !pending && hasContent;
  const onKey = (e: React.KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && canSend) { e.preventDefault(); sendMut.mutate(); }
  };
  /** The paperclip's one way in. A pasted picture takes the same. */
  const attach = (f: File) => { setFile(f); voice.discard(); };
  // An emoji goes where the cursor is — over a selection, if any — and the
  // cursor lands right after it, the box still focused (2026-10-18).
  const insertEmoji = (e: string) => {
    const el = areaRef.current;
    const next = insertAtCursor(text, el?.selectionStart, el?.selectionEnd, e);
    setText(next.value); setTouched(true); setSent(null);
    requestAnimationFrame(() => {
      const box = areaRef.current;
      if (box) { box.focus(); box.setSelectionRange(next.caret, next.caret); }
    });
  };
  // ⌘V a picture (owner, 2026-09-29): it lands where the paperclip puts a file
  // — same chip, same send, same limits (the worker's). A text paste is left to
  // the browser; text that came WITH the picture (a spreadsheet, a web page)
  // pastes too. The paperclip takes one file, so of several the first goes.
  const onPaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const images = pastedImages(e.clipboardData);
    if (!images.length) return;
    attach(images[0]);
    setFirstOfMany(images.length > 1 ? images[0] : null);
    if (!pastedWords(e.clipboardData).trim()) e.preventDefault();
  };
  const iconBtn = 'h-10 w-10 shrink-0 rounded-full flex items-center justify-center hover:bg-black/5 dark:hover:bg-white/10 disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00a884]';

  return (
    <div style={{ backgroundColor: WA.bar }} className="px-3 py-2 space-y-2">
      {replyTo && (
        <div className="flex items-stretch rounded-lg overflow-hidden bg-white/70 dark:bg-[var(--wa-field)]">
          <span className="w-1 shrink-0" style={{ backgroundColor: WA.quoteBar }} />
          <div className="min-w-0 flex-1 px-3 py-1.5">
            <p className="text-[12.8px] font-medium" style={{ color: NAME_COLOR[replyTo.sender ?? 'client'] }}>{t(senderLabel(replyTo.sender))}</p>
            <p className="text-[13px] line-clamp-2 break-words" style={{ color: WA.meta }}>{replyTo.body || replyTo.transcript || t('(fichier)')}</p>
          </div>
          <button type="button" onClick={onClearReply} className="px-3" style={{ color: WA.meta }} aria-label={t('Annuler la réponse')}>
            <Icon name="x" className="h-4 w-4" />
          </button>
        </div>
      )}

      {file && (
        <div className="flex items-center gap-2 rounded-lg bg-[var(--wa-field)] px-3 py-2">
          <span aria-hidden>{kindForFile(file) === 'image' ? '🖼️' : kindForFile(file) === 'video' ? '🎬' : '📄'}</span>
          <span className="text-[13px] truncate flex-1" style={{ color: WA.text }}>{file.name}</span>
          <span className="text-[11.5px] tabular-nums" style={{ color: WA.meta }}>{fmtSize(file.size)}</span>
          <button type="button" onClick={() => setFile(null)} style={{ color: WA.meta }} aria-label={t('Retirer le fichier')}>
            <Icon name="x" className="h-4 w-4" />
          </button>
        </div>
      )}
      {file && file === firstOfMany && (
        <p className="px-1 text-[12px]" style={{ color: WA.meta }}>{t('Une seule image part à la fois : la première est jointe.')}</p>
      )}

      {voice.recording && (
        <div className="flex items-center gap-2 rounded-lg bg-[var(--wa-field)] px-2 py-1">
          <VoicePlayer url={voice.recording.url} tone={WA.greenDark} />
          <span className="flex-1" />
          <button type="button" onClick={voice.discard} className="px-2" style={{ color: WA.meta }} aria-label={t('Supprimer le vocal')}>
            <Icon name="x" className="h-4 w-4" />
          </button>
        </div>
      )}
      {voice.error && <p className="text-[12px]" style={{ color: WA.error }} role="alert">{voice.error}</p>}

      <div className="flex items-end gap-1.5">
        <input
          ref={fileRef} type="file" className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) attach(f); e.target.value = ''; }}
        />
        <button type="button" onClick={() => fileRef.current?.click()} disabled={voice.state === 'recording'}
          className={iconBtn} style={{ color: WA.icon }} title={t('Joindre un fichier')} aria-label={t('Joindre un fichier')}>
          <Icon name="clip" className="h-6 w-6" />
        </button>

        {emoji && voice.state !== 'recording' && (
          <div className="relative shrink-0">
            <button type="button" data-emoji-toggle onClick={() => setEmojiOpen((o) => !o)} aria-expanded={emojiOpen}
              className={iconBtn} style={{ color: emojiOpen ? WA.green : WA.icon }} title={t('Emojis')} aria-label={t('Emojis')}>
              <Icon name="smile" className="h-6 w-6" />
            </button>
            {emojiOpen && (
              <EmojiPicker className="absolute bottom-full mb-2 left-0" onPick={insertEmoji} onClose={() => setEmojiOpen(false)} />
            )}
          </div>
        )}

        {voice.state === 'recording' ? (
          <div className="flex-1 flex items-center gap-3 rounded-lg bg-[var(--wa-field)] px-3 h-10">
            <span className="h-2.5 w-2.5 rounded-full animate-pulse" style={{ backgroundColor: WA.error }} aria-hidden />
            <span className="text-[14px] tabular-nums" style={{ color: WA.text }}>{mmss(voice.seconds)}</span>
            <span className="flex-1" />
            <button type="button" onClick={voice.discard} className="text-[13px]" style={{ color: WA.meta }}>{t('Annuler')}</button>
          </div>
        ) : (
          <textarea
            ref={areaRef}
            value={text}
            onChange={(e) => { setText(e.target.value); setTouched(true); setSent(null); }}
            onKeyDown={onKey}
            onPaste={onPaste}
            rows={Math.min(8, Math.max(1, text.split('\n').length))}
            maxLength={4096}
            placeholder={file ? t('Ajouter une légende…') : voice.recording ? t('Texte à envoyer après le vocal (facultatif)…') : t('Écrire un message')}
            // 16 px on a phone: anything smaller and iOS zooms the page on focus.
            className="flex-1 min-h-12 md:min-h-[3.5rem] max-h-[60vh] resize-y rounded-lg bg-[var(--wa-field)] px-3 py-2.5 text-[16px] md:text-[14.2px] leading-5 focus:outline-none placeholder:text-[var(--wa-placeholder)]"
            style={{ color: WA.text }}
          />
        )}

        {onSchedule && hasContent && voice.state !== 'recording' && (
          <div className="relative shrink-0">
            <button type="button" data-schedule-toggle onClick={() => { scheduleMut.reset(); setPicking((p) => !p); }}
              disabled={pending || scheduleMut.isPending || !textOnly || !text.trim()}
              className={iconBtn} style={{ color: picking ? WA.green : WA.icon }} aria-expanded={picking} aria-haspopup="dialog"
              title={textOnly ? t('Programmer l’envoi') : t('Seuls les messages texte peuvent être programmés')}
              aria-label={t('Programmer l’envoi')}>
              <ScheduleIcon name="clock" className="h-6 w-6" />
            </button>
            {picking && (
              <SchedulePicker
                busy={scheduleMut.isPending}
                error={scheduleMut.isError ? (scheduleMut.error as Error).message : null}
                onPick={(ms) => scheduleMut.mutate(ms)}
                onClose={() => setPicking(false)}
              />
            )}
          </div>
        )}

        {voice.state === 'recording' ? (
          <button type="button" onClick={voice.stop} className={`${iconBtn} text-white`} style={{ backgroundColor: WA.green }}
            title={t('Arrêter l’enregistrement')} aria-label={t('Arrêter l’enregistrement')}>
            <span className="h-3.5 w-3.5 rounded-sm bg-white dark:bg-white" aria-hidden />
          </button>
        ) : hasContent ? (
          // Stays the send button while it goes (a spinner), rather than
          // turning into the microphone mid-send.
          <button type="button" onClick={() => sendMut.mutate()} disabled={!canSend} className={`${iconBtn} text-white`} style={{ backgroundColor: WA.green }}
            title={t('Envoyer')} aria-label={t('Envoyer')}>
            {pending ? <span className="h-4 w-4 rounded-full border-2 border-white/60 border-t-transparent animate-spin" /> : <Icon name="send" className="h-5 w-5" />}
          </button>
        ) : (
          <button type="button" onClick={() => { setFile(null); void voice.start(); }} disabled={pending}
            className={iconBtn} style={{ color: WA.icon }} title={t('Enregistrer un vocal')} aria-label={t('Enregistrer un vocal')}>
            <Icon name="mic" className="h-6 w-6" />
          </button>
        )}
      </div>

      <div className="flex items-center gap-2 flex-wrap px-1">
        {/* ⚠️ THE BADGE BELONGS TO A MESSAGE (2026-09-04): it shows only while
            the composer is exactly as that send left it — empty, no file, no
            recording — and it says WHEN. Typing, a file or a new chat clears it. */}
        {sentBadge && (<>
          {sent && !text && !file && !voice.recording && voice.state !== 'recording' && (
            <span className="text-[12px] font-medium" style={{ color: WA.sent }}>{sentBadge(sent)}</span>
          )}
        </>)}
        {scheduledFor != null && !text && !file && !voice.recording && (
          <span className="text-[12px] font-medium inline-flex items-center gap-1" style={{ color: WA.sent }}>
            <ScheduleIcon name="clock" className="h-3.5 w-3.5" />
            {t('Programmé')} · {when(scheduledFor, Date.now())} — {t('il partira tout seul')}
          </span>
        )}
        {sendMut.isError && (
          <span className="text-[12px]" style={{ color: WA.error }} role="alert">{t('Échec de l’envoi :')} {(sendMut.error as Error).message}</span>
        )}
        {/* A refused schedule stays in sight once the picker is closed — nothing was saved. */}
        {scheduleMut.isError && !picking && (
          <span className="text-[12px]" style={{ color: WA.error }} role="alert">{t('Pas programmé — rien n’est enregistré :')} {(scheduleMut.error as Error).message}</span>
        )}
        <span className="flex-1" />
        {/* The keyboard-shortcut line means nothing on a phone; it only costs a row. */}
        <span className="hidden md:inline text-[11px]" style={{ color: WA.meta }}>{hint}</span>
      </div>
    </div>
  );
}

/** Conversations' Send: through the relay, exactly as typed; pauses the agent (A2). */
function sendToRelay(chatId: string, o: Outgoing) {
  if (o.voice) {
    const ext = o.voice.mime.includes('ogg') ? 'ogg' : o.voice.mime.includes('mp4') ? 'm4a' : 'webm';
    return whatsappApi.sendMedia(chatId, { file: o.voice.blob, filename: `vocal.${ext}`, kind: 'voice', caption: o.text, quotedId: o.quotedId });
  }
  if (o.file) return whatsappApi.sendMedia(chatId, { file: o.file, filename: o.file.name, kind: kindForFile(o.file), caption: o.text, quotedId: o.quotedId });
  return whatsappApi.sendReply(chatId, o.text.trim(), o.quotedId);
}

function RelayComposer({ chatId, suggestion, replyTo, onClearReply, prefill, onPending, emoji = false }: {
  chatId: string; suggestion: string; replyTo: WhatsAppMessage | null; onClearReply: () => void; prefill: ComposerPrefill;
  /** The emoji button — where reactions are on for this chat (2026-10-18). */
  emoji?: boolean;
  /** Shows what was just sent at once; `drop` takes it back if the send failed. */
  onPending?: { add: (p: PendingSend) => void; drop: (id: string) => void };
}) {
  const { t } = useT();
  const qc = useQueryClient();
  const can = useCan();
  if (!can('conversation.send')) {
    return <p className="px-4 py-3 text-[12.5px]" style={{ backgroundColor: WA.bar, color: WA.meta }}>{t('Tu ne peux pas envoyer de messages depuis ce compte.')}</p>;
  }
  return (<>
    <ScheduledStrip chatId={chatId} />
    <Composer
      chatId={chatId} suggestion={suggestion} prefill={prefill} replyTo={replyTo} onClearReply={onClearReply} emoji={emoji}
      onSchedule={async (o, sendAtIso) => {
        const out = await whatsappApi.scheduleReply(chatId, o.text.trim(), sendAtIso, o.quotedId);
        void qc.invalidateQueries({ queryKey: ['whatsapp-scheduled', chatId] });
        return out;
      }}
      onSend={async (o) => {
        const p: PendingSend = {
          id: `pending-${Date.now()}`, atMs: Date.now(), media: Boolean(o.file || o.voice),
          body: o.text?.trim() || (o.voice ? '🎤 Message vocal' : o.file ? `📎 ${o.file.name}` : ''),
        };
        onPending?.add(p);
        try { return await sendToRelay(chatId, o); } catch (e) { onPending?.drop(p.id); throw e; }
      }}
      onSent={() => {
        void qc.invalidateQueries({ queryKey: ['whatsapp-conversation', chatId] });
        void qc.invalidateQueries({ queryKey: ['whatsapp-conversations'] });
      }}
      sentBadge={(sent) => <>{t('✓ Envoyé')} {sentAgo(sent.at, t)} — {t('l’agent est en pause sur cette conversation')}</>}
      hint={<>{t('Envoyé tel quel. Envoyer d’ici met l’agent en pause, comme depuis le téléphone — sauf si vous envoyez sa suggestion sans la modifier.')} · ⌘/Ctrl + Entrée · {t('coller une image : ⌘/Ctrl + V')} · {t('🕘 pour programmer')}</>}
    />
  </>);
}

/**
 * The thread itself — wallpaper, day separators, bubbles, a picture full size
 * on click — with the composer under it. Shared by Conversations and the
 * Simulator (2026-10-01); `options` hangs each caller's actions on a message.
 */
export function ThreadFrame({ resetKey, title, messages, options, after = null, empty = null, composer, fill = false }: {
  /** A new value scrolls back to the newest message (a different chat). */
  resetKey: string;
  title?: string | null;
  messages: WhatsAppMessage[];
  options?: (m: WhatsAppMessage) => BubbleOptions;
  /** Under the newest message — the Simulator's « écrit… ». */
  after?: ReactNode;
  /** In place of « Aucun message. ». */
  empty?: ReactNode;
  composer: ReactNode;
  /** Fill the parent's height (the phone's full-screen chat): no frame, the
   *  thread takes what is left, the composer sits on the bottom safe area. */
  fill?: boolean;
}) {
  const { t } = useT();
  const [zoom, setZoom] = useState<{ src: string; alt: string } | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  // Was the reader at the newest message? Then a resize (the keyboard opening,
  // a picture loading) keeps them there, as WhatsApp does.
  const atBottomRef = useRef(true);
  const days = useMemo(() => groupByDay(messages, atOf), [messages]);
  const lastId = messages[messages.length - 1]?.id;
  const hasAfter = Boolean(after);

  // Open at the newest message, and follow new ones.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
    atBottomRef.current = true;
  }, [resetKey, lastId, hasAfter]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => { if (atBottomRef.current) el.scrollTop = el.scrollHeight; });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div className={fill ? 'flex flex-col h-full min-h-0' : 'rounded-xl overflow-hidden border border-[var(--wa-frame)]'} style={{ fontFamily: FONT }}>
      {title && (
        <div className="px-4 py-2.5 flex items-center gap-3" style={{ backgroundColor: WA.bar }}>
          <span className="h-9 w-9 rounded-full bg-[var(--wa-avatar)] flex items-center justify-center text-[15px]" aria-hidden>👤</span>
          <p className="text-[15px] font-medium truncate" style={{ color: WA.text }}>{title}</p>
        </div>
      )}
      {/* ⚠️ data-no-ptr: pulling down in a thread reads older messages — it
          must never trigger the shell's pull-to-refresh (shared contract). */}
      <div ref={scrollRef} data-no-ptr
        onScroll={(e) => { const el = e.currentTarget; atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 48; }}
        className={fill ? 'flex-1 min-h-0 overflow-y-auto overscroll-contain px-[4%] py-3' : 'max-h-[560px] min-h-[280px] overflow-y-auto px-[6%] py-3'}
        style={{ backgroundColor: WA.wallpaper }}>
        {days.map((d) => (
          <section key={d.key}>
            <div className="sticky top-0 z-10 flex justify-center py-2">
              <span className="text-[12.5px] rounded-lg px-3 py-1.5 shadow-[0_1px_0.5px_rgba(11,20,26,0.13)]"
                style={{ backgroundColor: WA.chip, color: WA.chipInk }}>
                {t(d.label)}
              </span>
            </div>
            {d.items.map((m, i) => {
              const prev = d.items[i - 1];
              const first = !prev || (prev.sender ?? prev.direction) !== (m.sender ?? m.direction);
              return (
                <Bubble key={m.id} m={m} first={first} onZoom={(src, alt) => setZoom({ src, alt })} {...options?.(m)} />
              );
            })}
          </section>
        ))}
        {!messages.length && (empty ?? (
          <p className="text-center py-10">
            <span className="text-[12.5px] rounded-lg px-3 py-1.5" style={{ backgroundColor: WA.empty, color: WA.chipInk }}>{t('Aucun message.')}</span>
          </p>
        ))}
        {after}
      </div>
      {fill
        ? <div className="shrink-0" style={{ backgroundColor: WA.bar, paddingBottom: 'var(--adm-safe-bottom, env(safe-area-inset-bottom, 0px))' }}>{composer}</div>
        : composer}
      {zoom && <Lightbox src={zoom.src} alt={zoom.alt} onClose={() => setZoom(null)} />}
    </div>
  );
}

export function ChatThread({ chatId, messages, suggestion, title, prefill = null, fill = false, reactions = false }: {
  chatId: string; messages: WhatsAppMessage[]; suggestion: string; title?: string | null; prefill?: ComposerPrefill;
  /** The phone's full-screen chat — see ThreadFrame. */
  fill?: boolean;
  /** ❤️ and the emoji picker (2026-10-18) — the worker's `takeover.reactions` for this chat. */
  reactions?: boolean;
}) {
  const { t } = useT();
  const can = useCan();
  const qc = useQueryClient();
  const [replyTo, setReplyTo] = useState<WhatsAppMessage | null>(null);

  // ─── « Supprimer pour tout le monde » (owner, 2026-09-29) ─────────────────
  // One question first — she loses the message, we cannot send it back. The
  // worker queues it for the relay and the thread's refetch shows it pending,
  // then deleted (or why not). A refusal (too late, no id yet) sits on THAT
  // message. Never pauses the agent.
  const [delError, setDelError] = useState<{ id: string; message: string } | null>(null);
  const delMut = useMutation({
    mutationFn: (m: WhatsAppMessage) => whatsappApi.deleteMessage(chatId, m.wa_message_id),
    onMutate: () => setDelError(null),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['whatsapp-conversation', chatId] }); },
    onError: (e, m) => setDelError({ id: m.id, message: (e as Error).message }),
  });
  const mayDelete = can('conversation.delete_message');
  const askDelete = (m: WhatsAppMessage) => {
    if (window.confirm(t('Supprimer ce message chez la cliente aussi ?'))) delMut.mutate(m);
  };

  // ─── ❤️ A REACTION (owner, 2026-10-18) ─────────────────────────────────────
  // Sent as a real WhatsApp reaction through the relay; the chip shows on the
  // next read. NOT a reply: it never pauses the agent. A refusal sits on THAT
  // message.
  const [reactError, setReactError] = useState<{ id: string; message: string } | null>(null);
  const reactMut = useMutation({
    mutationFn: ({ m, emoji }: { m: WhatsAppMessage; emoji: string }) => whatsappApi.react(chatId, m.quotable_id ?? m.wa_message_id, emoji),
    onMutate: () => setReactError(null),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['whatsapp-conversation', chatId] }); },
    onError: (e, v) => setReactError({ id: v.m.id, message: (e as Error).message }),
  });
  const mayReact = reactions && can('conversation.send');

  useEffect(() => { setReplyTo(null); setDelError(null); setReactError(null); }, [chatId]);

  // ─── WHAT YOU JUST SENT, AT ONCE (owner, 2026-10-07) ─────────────────────
  // On the thread the moment Send is pressed (🕓), replaced by WhatsApp's own
  // copy when it lands; while one is waiting the thread re-reads every 2 s
  // instead of 15.
  const [pending, setPending] = useState<PendingSend[]>([]);
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => { setPending([]); }, [chatId]);
  const stillOpen = openPending(messages, pending);
  useEffect(() => {
    if (stillOpen.length !== pending.length) setPending(stillOpen);
  }, [messages]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!stillOpen.length) return;
    const id = window.setInterval(() => {
      setNowMs(Date.now());
      void qc.invalidateQueries({ queryKey: ['whatsapp-conversation', chatId] });
    }, 2000);
    return () => window.clearInterval(id);
  }, [stillOpen.length, chatId, qc]);
  const shown = stillOpen.length ? [...messages, ...pendingAsMessages(stillOpen, nowMs)] : messages;

  return (
    <ThreadFrame
      resetKey={chatId} title={title} messages={shown} fill={fill}
      options={(m) => ({
        onReply: setReplyTo,
        onDelete: mayDelete && canOfferDelete(m) ? askDelete : undefined,
        deleting: delMut.isPending && delMut.variables?.id === m.id,
        deleteError: delError?.id === m.id ? delError.message : null,
        onReact: mayReact ? (msg, emoji) => reactMut.mutate({ m: msg, emoji }) : undefined,
        reactError: reactError?.id === m.id ? reactError.message : null,
      })}
      composer={<RelayComposer chatId={chatId} suggestion={suggestion} replyTo={replyTo} onClearReply={() => setReplyTo(null)} prefill={prefill} emoji={reactions}
        onPending={{ add: (p) => { setNowMs(Date.now()); setPending((l) => [...l, p]); }, drop: (id) => setPending((l) => l.filter((x) => x.id !== id)) }} />}
    />
  );
}
